import React, { useState, useMemo, useRef, useEffect } from 'react';
import { MemberModel, ScheduleEvent, SiteSettingModel, ScheduleMarker } from '../../../../models';
import {
    Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check, Circle, Trash2, Plus,
    ChevronDown, Download, CalendarRange, Trash, MoreVertical, FileText, Settings, X, ArrowRight
} from 'lucide-react';
import Header from '../studentFrame/header';



interface StudentScheduleProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    schedules: ScheduleEvent[];
    recycleBinSchedules?: ScheduleEvent[];
    scheduleMarkers?: ScheduleMarker[]; // Added props
    rangeSelection: { start: Date | null; end: Date | null };
    onRangeSelect: (range: { start: Date | null; end: Date | null }) => void;
    onUpdateSchedule?: (id: string, updates: Partial<ScheduleEvent>) => void;
    onSaveSchedules?: (events: ScheduleEvent[]) => void;
    onDeleteSchedules?: (ids: string[]) => void;
    onRestoreSchedules?: (ids: string[]) => void;
    onPermanentDeleteSchedules?: (ids: string[]) => void;
    onSaveMarker?: (marker: ScheduleMarker) => void;
    onDeleteMarker?: (id: string) => void;
    onViewDetail: (event: ScheduleEvent) => void;
    onOpenManager: () => void;
    isReadOnly?: boolean;
}

const ScheduleMain: React.FC<StudentScheduleProps> = ({
    member, siteSettings, schedules, recycleBinSchedules = [],
    scheduleMarkers = [], rangeSelection, onRangeSelect,
    onUpdateSchedule, onSaveSchedules, onViewDetail,
    onDeleteSchedules, onRestoreSchedules, onPermanentDeleteSchedules,
    onSaveMarker, onDeleteMarker, onOpenManager,
    isReadOnly = false
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    // Local rangeSelection state removed
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

    const calendarDays = useMemo(() => {
        const days = [];
        const totalDays = daysInMonth(currentMonth);
        const startOffset = firstDayOfMonth(currentMonth);
        for (let i = 0; i < startOffset; i++) days.push(null);
        for (let i = 1; i <= totalDays; i++) days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
        while (days.length % 7 !== 0) days.push(null);
        return days;
    }, [currentMonth]);

    const changeMonth = (offset: number) => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1));
    };

    const getEventsForDate = (date: Date) => {
        return schedules.filter(s => {
            const sDate = new Date(s.date);
            return sDate.getFullYear() === date.getFullYear() &&
                sDate.getMonth() === date.getMonth() &&
                sDate.getDate() === date.getDate();
        });
    };

    const toggleStatus = (event: ScheduleEvent) => {
        if (onUpdateSchedule) {
            const nextStatus = event.status === 'completed' ? 'pending' : 'completed';
            onUpdateSchedule(event.id, { status: nextStatus });
        }
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date); // Always update view for daily tasks

        if (!rangeSelection.start || (rangeSelection.start && rangeSelection.end)) {
            // Start new range or restart
            onRangeSelect({ start: date, end: null });
        } else {
            // Complete the range
            if (date < rangeSelection.start) {
                onRangeSelect({ start: date, end: null });
            } else {
                onRangeSelect({ start: rangeSelection.start, end: date });
            }
        }
    };

    const clearRangeSelection = () => {
        onRangeSelect({ start: null, end: null });
    };

    const getRangeDuration = () => {
        if (!rangeSelection.start || !rangeSelection.end) return 0;
        const diffTime = Math.abs(rangeSelection.end.getTime() - rangeSelection.start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    const handleDownloadPDF = () => {
        window.print();
        setIsMenuOpen(false);
    };

    const toLocaDateString = (date: Date) => {
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offset).toISOString().split('T')[0];
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 relative pb-20 sm:pb-0 print:bg-white print:p-0">
            {/* Header */}
            {/* Header */}
            <Header
                icon={CalendarIcon}
                title="학습 일정"
                subtitle="오늘의 학습 목표를 달성해보세요"
                rightElement={
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">


                        <div className="flex bg-slate-100 p-1 rounded-xl">
                            <button onClick={() => changeMonth(-1)} className="p-2 text-slate-400 hover:bg-white hover:text-slate-600 rounded-lg transition-all shadow-sm">
                                <ChevronLeft size={16} />
                            </button>
                            <span className="px-4 py-2 text-sm font-black text-slate-600 min-w-[100px] text-center bg-white rounded-lg shadow-sm mx-1">
                                {currentMonth.getFullYear()}.{String(currentMonth.getMonth() + 1).padStart(2, '0')}
                            </span>
                            <button onClick={() => changeMonth(1)} className="p-2 text-slate-400 hover:bg-white hover:text-slate-600 rounded-lg transition-all shadow-sm">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                }
            />

            {/* Print Header */}
            <div className="hidden print:block mb-8 p-4 border-b-2 border-slate-900">
                <h1 className="text-2xl font-black text-slate-900 mb-1">{member.name} 학생 학습 일정표</h1>
                <p className="text-sm font-bold text-slate-500">{currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월</p>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden mx-4 mt-4 print:mx-0 print:mt-0 print:shadow-none print:border-none">
                {/* Days of Week */}
                <div className="grid grid-cols-7 border-b border-slate-50 bg-slate-50/50">
                    {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                        <div key={i} className={`py-3 text-center text-[10px] font-black ${i === 0 ? 'text-rose-500' : i === 6 ? 'text-indigo-500' : 'text-slate-400'}`}>
                            {d}
                        </div>
                    ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 auto-rows-fr bg-slate-50/30">
                    {calendarDays.map((day, idx) => {
                        if (!day) return <div key={`empty-${idx}`} className="aspect-[3/4] sm:aspect-square bg-slate-50/10"></div>;
                        const events = getEventsForDate(day);
                        const isToday = new Date().toDateString() === day.toDateString();
                        const isSelectedDate = selectedDate && day.toDateString() === selectedDate.toDateString();

                        // Range Logic
                        const isRangeStart = rangeSelection.start && day.toDateString() === rangeSelection.start.toDateString();
                        const isRangeEnd = rangeSelection.end && day.toDateString() === rangeSelection.end.toDateString();
                        const isInRange = rangeSelection.start && rangeSelection.end && day > rangeSelection.start && day < rangeSelection.end;

                        // Determine styling based on range status
                        let containerStyle = "bg-white hover:bg-slate-50";
                        if (isInRange) containerStyle = "bg-indigo-50/50";
                        if (isRangeStart || isRangeEnd) containerStyle = "bg-indigo-50 ring-2 ring-inset ring-indigo-500 z-10";
                        else if (isSelectedDate && !isRangeStart && !isRangeEnd && !isInRange) containerStyle = "ring-2 ring-inset ring-slate-200 z-10";

                        // Markers Logic
                        const markersOnDay = scheduleMarkers.filter(m => {
                            const dStr = day.toISOString().split('T')[0];
                            return dStr >= m.startDate && dStr <= m.endDate;
                        });

                        return (
                            <div
                                key={idx}
                                onClick={() => handleDateClick(day)}
                                className={`
                                cursor-pointer relative
                                aspect-[3/4] sm:aspect-square border-[0.5px] border-slate-50 p-1.5 sm:p-2 flex flex-col items-center sm:items-start group transition-all
                                ${containerStyle}
                                ${isToday && !isRangeStart && !isRangeEnd ? 'bg-indigo-50/10' : ''}
                            `}
                            >
                                <span className={`text-[10px] font-black mb-1 w-5 h-5 flex items-center justify-center rounded-full transition-all
                                ${isRangeStart || isRangeEnd
                                        ? 'bg-indigo-600 text-white shadow-md'
                                        : isToday
                                            ? 'bg-rose-500 text-white shadow-md'
                                            : isSelectedDate
                                                ? 'text-indigo-600 bg-indigo-50'
                                                : 'text-slate-400'}
                                `}>
                                    {day.getDate()}
                                </span>

                                {/* Markers Display */}
                                <div className="w-full flex flex-col gap-0.5 mb-1">
                                    {markersOnDay.map((m, midx) => {
                                        let bg = 'bg-slate-100 text-slate-500';
                                        if (m.type === 'vacation') bg = 'bg-rose-100 text-rose-600';
                                        else if (m.type === 'exam') bg = 'bg-amber-100 text-amber-700';
                                        else if (m.type === 'makeup') bg = 'bg-emerald-100 text-emerald-700';

                                        return (
                                            <div key={m.id} className={`h-3 sm:h-4 w-full rounded-sm ${bg} text-[8px] sm:text-[9px] px-1 flex items-center overflow-hidden whitespace-nowrap`}>
                                                <span className="font-bold truncate w-full">
                                                    {m.title}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Desktop Events */}
                                <div className="hidden sm:block flex-1 w-full space-y-0.5 overflow-y-auto custom-scrollbar no-scrollbar">
                                    {events.map((e) => (
                                        <div
                                            key={e.id}
                                            className={`w-full text-left px-0.5 py-px truncate text-[10px] font-bold transition-all ${e.status === 'completed' ? 'text-slate-300 line-through' : 'text-slate-600'}`}
                                        >
                                            <span className={`mr-1 ${e.status === 'completed' ? 'text-slate-300' : 'text-indigo-500'}`}>
                                                ·
                                            </span>
                                            <span className={`${e.status === 'completed' ? 'text-slate-300' : 'text-indigo-600'} mr-1`}>
                                                {e.subject}
                                            </span>
                                            {e.bookTitle} {e.task}
                                        </div>
                                    ))}
                                </div>

                                {/* Mobile Indicator */}
                                <div className="sm:hidden flex flex-col gap-0.5 items-center mt-auto w-full">
                                    <div className="flex gap-0.5 justify-center flex-wrap max-w-full px-1">
                                        {events.slice(0, 4).map((e, i) => (
                                            <div key={i} className={`w-1 h-1 rounded-full ${e.status === 'completed' ? 'bg-emerald-400' : 'bg-indigo-400'}`}></div>
                                        ))}
                                    </div>
                                    {events.length > 4 && <div className="text-[6px] text-slate-300 font-bold leading-none">+</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Range Selection Bar */}
            {rangeSelection.start && rangeSelection.end && (
                <div className="mx-4 mt-4 bg-slate-800 text-white p-4 rounded-xl shadow-xl flex items-center justify-between animate-in fade-in slide-in-from-bottom-2">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center text-indigo-400">
                            <CalendarRange size={20} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 text-sm font-bold">
                                <span>{rangeSelection.start.toLocaleDateString()}</span>
                                <ArrowRight size={14} className="text-slate-400" />
                                <span>{rangeSelection.end.toLocaleDateString()}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 font-medium">
                                총 {getRangeDuration()}일 선택됨
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isReadOnly && (
                            <button
                                onClick={onOpenManager}
                                className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-xs font-black shadow-lg shadow-indigo-500/30 transition-all flex items-center gap-2"
                            >
                                <Settings size={14} />
                                일정 설정
                            </button>
                        )}
                        <button
                            onClick={clearRangeSelection}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-700 text-slate-400 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>
            )}

            {/* Selected Daily Schedule Section (Mobile & Desktop) */}
            <div className="space-y-3 px-4 mt-4 pb-4 print:hidden">
                <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-100 shadow-sm transition-all hover:shadow-md">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white shadow-md shadow-indigo-200">
                            <span className="text-xs font-black">{selectedDate.getDate()}</span>
                        </div>
                        <div>
                            <h3 className="text-xs font-black text-slate-800">
                                {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일의 할 일
                            </h3>
                            <p className="text-[10px] font-bold text-slate-400">
                                총 {getEventsForDate(selectedDate).length}개의 일정이 있습니다.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="space-y-1.5">
                    {getEventsForDate(selectedDate).length > 0 ? (
                        getEventsForDate(selectedDate).map((e) => (
                            <div
                                key={e.id}
                                onClick={() => onViewDetail(e)}
                                className={`group cursor-pointer flex items-center gap-3 p-3 rounded-xl border transition-all hover:scale-[1.01] active:scale-[0.99] ${e.status === 'completed'
                                    ? 'bg-emerald-50/50 border-emerald-100 opacity-70'
                                    : 'bg-white border-slate-100 shadow-sm hover:shadow-md hover:border-indigo-100'
                                    }`}
                            >
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${e.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-50 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-500'
                                    }`}>
                                    {e.status === 'completed' ? <Check size={18} /> : <Circle size={18} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${e.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-50 text-indigo-600'
                                            }`}>
                                            {e.subject}
                                        </span>
                                        <span className="text-[10px] text-slate-400 font-bold truncate">{e.bookTitle}</span>
                                    </div>
                                    <p className={`text-xs font-bold truncate transition-colors ${e.status === 'completed' ? 'text-emerald-800 line-through decoration-emerald-500/50' : 'text-slate-800 group-hover:text-indigo-900'
                                        }`}>
                                        {e.task}
                                    </p>
                                </div>
                                <ChevronRight size={16} className="text-slate-200 group-hover:text-indigo-300 transition-colors" />
                            </div>
                        ))
                    ) : (
                        <div className="py-8 flex flex-col items-center justify-center text-center bg-slate-50/50 rounded-xl border-2 border-dashed border-slate-100">
                            <p className="text-[11px] font-bold text-slate-400">일정이 없습니다.</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">달력에서 구간을 선택해서 일정을 정해주세요.</p>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
            @media print {
                body { background: white !important; }
                .custom-scrollbar::-webkit-scrollbar { display: none; }
                header, nav, aside, .print\\:hidden { display: none !important; }
                main { padding: 0 !important; margin: 0 !important; }
                .max-w-6xl { max-width: 100% !important; width: 100% !important; }
            }
        `}</style>
        </div>
    );
};

export default ScheduleMain;
